/**
 * applyArtifactDirective — the artifact.* sub-switch of core (B).
 *
 * Handles `artifact.init`, `artifact.status`, `artifact.page`, `artifact.block`,
 * and `artifact.coverage` SSE directives emitted by the x-agent's
 * ArtifactBuildSink. These build an `ArtifactContent` structure live inside
 * `ctx.activeArtifacts`, mirroring the way `applyReportDirective` builds a
 * `ReportConfig` in `ctx.activeReportModules`.
 *
 * Key differences from the report path:
 *  - NO API calls during streaming — artifact content lives entirely in
 *    `ctx.activeArtifacts` until `runChatStream`'s `complete` handler derives the
 *    `result.card` via `deriveArtifactCardActions`.
 *  - Pages module is focused (not the artifact itself) on the first block, so the
 *    user lands in the Pages chrome with the back arrow available.
 *
 * Returns true when the action was an `artifact.*` directive, false otherwise
 * (so `applyUiDirective` can fall through to future sub-appliers).
 */

import { queryKeys } from '@/lib/query/keys'
import type { ArtifactContent, ArtifactStatus, Page, Block, CoverageMap } from '@xavior/shared/studio'
import type { ActiveArtifactEntry } from './helpers/artifact-result-card'
import type { TurnContext, ChatStreamDeps } from './useChatStream.types'

type UiAction = { type: string; [key: string]: unknown }

/**
 * Apply an `artifact.*` directive. Returns `true` when handled, `false` when the
 * action type is not in this sub-switch.
 */
export async function applyArtifactDirective(
  ctx: TurnContext,
  action: UiAction,
  deps: ChatStreamDeps,
  _signal: AbortSignal,
): Promise<boolean> {
  const { workspaceId, queryClient, isTargetConversationActive } = deps

  if (action.type === 'artifact.init') {
    // Seed a skeleton ArtifactContent — blocks arrive via artifact.page/block.
    const draftId = `draft_${crypto.randomUUID()}`
    const entry: ActiveArtifactEntry = {
      content: {
        schemaVersion: 1,
        shape: (action.shape as ArtifactContent['shape']) || 'paged',
        kind: (action.kind as ArtifactContent['kind']) || 'blank',
        format: (action.format as ArtifactContent['format']) || 'slides',
        title: (action.title as string) || 'Untitled',
        pages: [],
      },
      themeId: (action.themeId as string) || 'xavior',
      brandKitId: action.brandKitId as string | undefined,
    }
    ctx.activeArtifacts.set(draftId, entry)
    ctx.activeArtifactId = draftId
    return true
  }

  if (action.type === 'artifact.status') {
    // Mirror into the chat thinking indicator (same as report.status).
    const message = typeof action.message === 'string' ? action.message : undefined
    if (message && isTargetConversationActive()) {
      deps.markXaviorStatusActivity()
      deps.setThinking(message)
    }
    return true
  }

  if (action.type === 'artifact.page') {
    const id = ctx.activeArtifactId
    if (!id) return true
    const entry = ctx.activeArtifacts.get(id)
    if (!entry) return true

    const pageData = action.page as Page | undefined
    if (!pageData || typeof pageData.id !== 'string') return true

    const existingIdx = entry.content.pages.findIndex((p) => p.id === pageData.id)
    if (existingIdx >= 0) {
      // Replace, but keep any blocks already applied (blocks arrive separately).
      entry.content.pages[existingIdx] = {
        ...pageData,
        blocks: entry.content.pages[existingIdx].blocks,
      }
    } else {
      entry.content.pages.push({ ...pageData, blocks: pageData.blocks ?? [] })
    }
    return true
  }

  if (action.type === 'artifact.block') {
    // Upsert a Block into its target page; focus Pages module on first block ever.
    const id = ctx.activeArtifactId
    if (!id) return true
    const entry = ctx.activeArtifacts.get(id)
    if (!entry) return true

    const blockData = action.block as Block | undefined
    const pageId = action.pageId as string | undefined
    if (!blockData || typeof blockData.id !== 'string') return true

    // Find target page (by explicit pageId, or the sole page for single-shape).
    let page: Page | undefined
    if (pageId) {
      page = entry.content.pages.find((p) => p.id === pageId)
    } else if (entry.content.pages.length === 1) {
      page = entry.content.pages[0]
    }
    if (!page) {
      console.warn('[useChat] artifact.block: target page not found, block dropped', { pageId, blockId: blockData.id })
      return true
    }

    // Detect "first block ever" for this artifact using the firstBlockFired flag
    // (replaces the O(n) reduce over all pages that ran on every block).
    const isFirstBlock = !entry.firstBlockFired

    const existingIdx = page.blocks.findIndex((b) => b.id === blockData.id)
    if (existingIdx >= 0) {
      page.blocks[existingIdx] = blockData
    } else {
      page.blocks.push(blockData)
    }

    if (isFirstBlock) entry.firstBlockFired = true

    // On first block: focus the Pages container module so the user lands in the
    // Pages chrome with the back arrow available (mirrors the report.section focus
    // on first section).
    if (isFirstBlock) {
      try {
        const cachedModules = queryClient.getQueryData<any[]>(
          queryKeys.modules.list(workspaceId),
        )
        const pagesModule = Array.isArray(cachedModules)
          ? cachedModules.find(
              (m: any) => m?.type === 'pages' && !m?.archived && !m?.archivedAt,
            )
          : undefined
        if (pagesModule?.id) {
          deps.onUiActions?.([
            {
              type: 'module.focus',
              moduleId: pagesModule.id as string,
              anchorMessageId: deps.assistantId,
              skipInvalidation: true,
            } as import('@/lib/api/endpoints/chat').ChatUIAction,
          ])
        }
      } catch (lookupErr) {
        console.warn('[useChat] Pages module lookup failed for artifact focus:', lookupErr)
      }
    }
    return true
  }

  if (action.type === 'artifact.coverage') {
    const id = ctx.activeArtifactId
    if (!id) return true
    const entry = ctx.activeArtifacts.get(id)
    if (!entry) return true

    if (action.coverage && typeof action.coverage === 'object') {
      entry.content.coverage = action.coverage as CoverageMap
    }
    return true
  }

  if (action.type === 'artifact.saved') {
    // PR-4: x-agent's ArtifactBuildSink persisted the identity row and emitted
    // artifact.saved{objectId} (one per turn, right after artifact.init). Stamp
    // the objectId onto the active entry so deriveArtifactCardActions threads it
    // into the result.card — Send/Publish then target the real
    // objects(type='artifact') row instead of an ephemeral draft.
    //
    // PR-5b: the SAME directive is RE-EMITTED once at turn end with a `status`
    // field carrying the TRUE terminal status the sink wrote (ready/draft/
    // archived) — read off the loosely-typed action defensively. The init-time
    // saved frame omits status (the row is still 'building'); only the terminal
    // re-emit stamps it, so the active entry ends the turn with the real status
    // that gates Send/Publish on the draft card.
    const id = ctx.activeArtifactId
    if (!id) return true
    const entry = ctx.activeArtifacts.get(id)
    if (!entry) return true
    if (typeof action.objectId === 'string' && action.objectId) {
      entry.objectId = action.objectId
    }
    if (typeof action.status === 'string' && action.status) {
      entry.status = action.status as ArtifactStatus
    }
    return true
  }

  return false
}
